import { View, Text, StyleSheet, Linking } from 'react-native'
import { router } from 'expo-router'
import { StandaloneWrapper } from '@/src/common/wrappers/StandaloneWrapper'
import { mesaColorNames, typography } from '@/src/common/theme'
import { Button } from '@/src/common/components/Button'
import { DesignSystem } from '@/src/design-system'
import { useState, useEffect } from 'react'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { formatNumber } from '@/src/common/utils/general'
import { useLogError } from '@/src/common/hooks/useLogError'
import { routerBack } from '@/src/common/utils/routerBack'
import * as Stores from '../stores'
import * as Navigation from '../navigation'

export function RedemptionConfirmationScreen() {
  const logError = useLogError()
  const Alert = useAlert(RedemptionConfirmationScreen.name)

  const [transferLoading, setTransferLoading] = useState<boolean>(false)

  const {
    mesaPointsToRedeem,
    partnerCredits,
    conversionRate,
    remainingPoints,
    idempotencyKey,
    selectedGuestId,
  } = Stores.usePointsToOmniStore()

  const [transfer] = Stores.PointsToOmniApi.endpoints['POST /points/transfer'].useMutation()

  // Validate required data
  useEffect(() => {
    if (
      !mesaPointsToRedeem ||
      !partnerCredits ||
      !conversionRate ||
      remainingPoints === null ||
      !idempotencyKey ||
      !selectedGuestId
    ) {
      logError(new Error('Missing required data for Omni transfer confirmation'), {
        mesaPointsToRedeem,
        partnerCredits,
        conversionRate,
        remainingPoints,
        idempotencyKey,
        selectedGuestId,
      })
    }
  }, [
    mesaPointsToRedeem,
    partnerCredits,
    conversionRate,
    remainingPoints,
    idempotencyKey,
    selectedGuestId,
    logError,
  ])

  // Check for insufficient points
  useEffect(() => {
    if (typeof remainingPoints === 'number' && remainingPoints < 0) {
      Alert.alert(
        'Insufficient Points Balance',
        `You do not have enough points to transfer ${formatNumber(mesaPointsToRedeem || 0)} points.`,
        [{ text: 'OK', onPress: () => void routerBack() }],
        { cancelable: false, closeableByOverlay: false, onDismiss: () => void routerBack() },
      )
    }
  }, [remainingPoints, mesaPointsToRedeem, Alert])

  const handleUsePoints = async () => {
    if (!idempotencyKey || !mesaPointsToRedeem || !selectedGuestId) {
      logError(new Error('Omni transfer fields are required but missing'))
      Alert.alert(
        'Oops, Something Went Wrong!',
        `We couldn't process your transfer at this time. Please try again later, or contact support if the issue persists.`,
        [
          {
            text: 'Contact Support',
            onPress: () => void Linking.openURL('mailto:support@mesamember.com'),
            style: 'default',
          },
          { text: 'Return', onPress: router.dismissAll, style: 'default' },
        ],
      )
      return
    }

    setTransferLoading(true)

    const result = await transfer({
      body: {
        partner: 'OMNI',
        mesaPointsToRedeem,
        idempotencyKey,
        transferPartnerId: selectedGuestId,
      },
    })

    if (!result.error) {
      router.push(Navigation.Routes.TransferSuccess.route)
    } else {
      logError(new Error('Omni transfer failed on submission'), { error: result.error })
      Alert.alert(
        'Oops, Something Went Wrong!',
        `We couldn't process your transfer at this time. Please try again later, or contact support if the issue persists.`,
        [
          {
            text: 'Contact Support',
            onPress: () => void Linking.openURL('mailto:support@mesamember.com'),
            style: 'default',
          },
          { text: 'Return', onPress: router.dismissAll, style: 'default' },
        ],
      )
      setTransferLoading(false)
      return
    }
  }

  const conversionText =
    mesaPointsToRedeem && partnerCredits
      ? `${mesaPointsToRedeem.toLocaleString()} Mesa Points = ${partnerCredits.toLocaleString()} Omni Credits`
      : '30,000 Mesa Points = 100 Omni Credits'

  return (
    <StandaloneWrapper
      screen={Navigation.Routes.RedemptionConfirmation.route}
      title="Confirm Points Transfer"
      profileProps={{ menuIconProps: { color: mesaColorNames['Mesa - Brand/White #FFFFFF'] } }}
      hideBackButton={false}
    >
      <DesignSystem.Card style={styles.confirmationCard}>
        <View style={[styles.infoSection, styles.infoSectionBorder]}>
          <Text style={styles.infoHeader}>Transfer To</Text>
          <Text style={typography['label/md-bold']}>Omni Hotel & Resorts</Text>
        </View>

        <View style={[styles.infoSection, styles.infoSectionBorder]}>
          <Text style={styles.infoHeader}>Conversion Rate</Text>
          <Text style={typography['label/md-bold']}>{conversionText}</Text>
        </View>

        <View style={[styles.infoSection, styles.infoSectionBorder]}>
          <Text style={styles.infoHeader}>Transfer Amount</Text>
          <Text style={typography['label/md-bold']}>
            {formatNumber(mesaPointsToRedeem || 0)} Points
          </Text>
        </View>

        <View style={styles.infoSection}>
          <Text style={styles.infoHeader}>Remaining Mesa Points</Text>
          <Text style={typography['label/md-bold']}>
            {formatNumber(Math.max(0, remainingPoints || 0))}
          </Text>
        </View>
      </DesignSystem.Card>

      <DesignSystem.Card style={styles.termsCard}>
        <Text style={typography['label/sm-regular']}>
          Typically your transfer will be processed within{' '}
          <Text style={typography['label/sm-bold']}>24 hours by Mesa</Text> however it may take up
          to{' '}
          <Text style={typography['label/sm-bold']}>
            7 business days for Omni to credit your Omni account
          </Text>{' '}
          based upon the date and time of scheduling.
        </Text>
        <Text style={typography['label/sm-regular']}>
          Points redemption for this benefit is final once submitted and cannot be reversed.
        </Text>
        <Text
          style={[
            typography['label/sm-regular'],
            { color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'] },
          ]}
          onPress={() =>
            void Linking.openURL(
              'https://mesamember.com/legal/mesa-membership-terms-and-conditions',
            )
          }
        >
          Reward Terms and Conditions
        </Text>
      </DesignSystem.Card>

      <Button
        title="Use Points"
        onPress={() => void handleUsePoints()}
        disabled={
          transferLoading ||
          !mesaPointsToRedeem ||
          !partnerCredits ||
          !conversionRate ||
          remainingPoints === null ||
          !idempotencyKey ||
          !selectedGuestId ||
          (remainingPoints || 0) < 0
        }
        loading={transferLoading}
        style={{ marginBottom: 40 }}
      />
    </StandaloneWrapper>
  )
}

const styles = StyleSheet.create({
  confirmationCard: {
    borderRadius: 12,
    padding: 16,
    gap: 16,
  },
  termsCard: {
    borderRadius: 12,
    padding: 16,
    gap: 24,
  },
  infoSection: {
    gap: 8,
    padding: 4,
  },
  infoSectionBorder: {
    borderBottomWidth: 1,
    borderBottomColor: mesaColorNames['semantic/bg/disabled #DADDDE'],
    paddingBottom: 16,
  },
  infoHeader: {
    ...typography['label/sm-regular'],
    color: mesaColorNames['Greyscale/500 #6E7375'],
  },
})
